"use client";

import { useState, useEffect } from "react";

type KitItem = {
    id: string;
    name: string;
    img: string;
    sport: string;
    index: number
};

const defaultItems: KitItem[] = [];

declare global {
    interface Window {
        addKitItem: (item: KitItem) => void;
        updateKitItem: (id: string, updates: Partial<Omit<KitItem, "id">>) => void;
        removeKitItem: (id: string) => void;
        getKitItems: () => KitItem[];
    }
}

type Position = "left" | "center" | "right" | "hidden-left" | "hidden-right";

export function KitPicker() {
    const [items, setItems] = useState<KitItem[]>(defaultItems);
    const [current, setCurrent] = useState(1);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        (window as any).addKitItem = (item: KitItem) => {
            setItems((prev) => [...prev, item]);
        };

        (window as any).updateKitItem = (id: string, updates: Partial<Omit<KitItem, "id">>) => {
            setItems((prev) =>
                prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
            );
        };

        (window as any).removeKitItem = (id: string) => {
            setItems((prev) => prev.filter((item) => item.id !== id));
        };

        (window as any).getKitItems = () => items;

        (window as any).setKitItemById = (id: string) => {
            const index = items.findIndex(item => item.id === "project-" + id);
            if (index === -1) return;

            setCurrent(index);

            //const selected = items[index];

            /*(window as any)["loadProject"](
                selected.id,
                selected.sport,
                selected.index
            );*/
        };

        return () => {
            delete (window as any).addKitItem;
            delete (window as any).updateKitItem;
            delete (window as any).removeKitItem;
            delete (window as any).getKitItems;
        };
    }, [items]);

    const mod = (n: number) => (n + items.length) % items.length;

    const goPrev = () => {
        if (isAnimating) return;
        setIsAnimating(true);
        const newIndex = mod(current - 1);
        setCurrent(newIndex);
        const newItem = items[newIndex];
        setTimeout(() => {
            (window as any)["loadProject"](newItem.id, newItem.sport, newItem.index);
        }, 500);
    };

    const goNext = () => {
        if (isAnimating) return;
        setIsAnimating(true);
        const newIndex = mod(current + 1);
        setCurrent(newIndex);
        const newItem = items[newIndex];
        setTimeout(() => {
            (window as any)["loadProject"](newItem.id, newItem.sport, newItem.index);
        }, 500);
    };

    useEffect(() => {
        if (isAnimating) {
            const timer = setTimeout(() => setIsAnimating(false), 400);
            return () => clearTimeout(timer);
        }
    }, [isAnimating]);

    const getPosition = (index: number): Position => {
        const prevIndex = mod(current - 1);
        const nextIndex = mod(current + 1);
        const farPrevIndex = mod(current - 2);
        const farNextIndex = mod(current + 2);

        if (index === current) return "center";
        if (index === prevIndex) return "left";
        if (index === nextIndex) return "right";
        if (index === farPrevIndex) return "hidden-left";
        if (index === farNextIndex) return "hidden-right";
        return "hidden-left";
    };

    /*const positionStyles: Record<Position, { x: number; opacity: number }> = {
        "hidden-left": { x: -154, opacity: 1 },
        left: { x: 0, opacity: 0.55 },
        center: { x: 154, opacity: 1 },
        right: { x: 308, opacity: 0.55 },
        "hidden-right": { x: 462, opacity: 0 },
    };*/

    // slot = 1/3 of container width; positions are slot-based
    const slot = (((window as any).innerWidth * (window as any).kitSize) * 0.8) / 3;
    const positionStyles: Record<Position, { x: number; opacity: number }> = {
        "hidden-left": { x: -slot, opacity: 0 },
        left: { x: 0, opacity: 1 },
        center: { x: slot, opacity: 1 },
        right: { x: slot * 2, opacity: 1 },
        "hidden-right": { x: slot * 3, opacity: 0 },
    };

    return (
        <div id="KitPicker" className="hidden bg-zinc-900 flex items-center justify-center z-10 relative left-0 !bg-transparent !w-[100%] text-primary h-[30%]">{/** */}
            <div
                className="flex flex-col items-center text-white outline-none w-full"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "ArrowLeft") goPrev();
                    if (e.key === "ArrowRight") goNext();
                }}
            >
                <div className="text-xl font-extrabold tracking-wide uppercase opacity-90 text-primary">
                    {items[current]?.name}
                </div>

                <div className="flex items-center gap-2 w-[100%]">
                    <button
                        onClick={goPrev}
                        disabled={isAnimating}
                        className="text-primary hover:text-white text-xl bg-transparent border-none cursor-pointer disabled:opacity-50 transition-colors w-[10%] z-10"
                    >
                        ◀
                    </button>

                    <div className=" w-[80%]">
                        <div className={`relative ${(window as any).isMobile ? "h-[180px]" : "h-[250px]"}`}>
                            {items.map((item, index) => {
                                const position = getPosition(index);
                                const isActive = position === "center";
                                const pos = positionStyles[position];

                                return (
                                    <div
                                        id={item.id}
                                        key={item.id}
                                        data-sport={item.sport}
                                        data-index={item.index}
                                        className="absolute left-0 flex flex-col items-center w-[33%] justify-center h-full"
                                        style={{
                                            backgroundPosition: "center",
                                            backgroundSize: "cover",
                                            backgroundImage: "url(" + item.img + ")",
                                            transform: `translateX(${pos.x}px) scale(${isActive ? 1.05 : 0.92})`,
                                            opacity: pos.opacity,
                                            zIndex: isActive ? 10 : 1,
                                            transition: "transform 400ms cubic-bezier(0.4, 0, 0.2, 1), opacity 400ms cubic-bezier(0.4, 0, 0.2, 1)",
                                        }}
                                    >
                                        {/**<img
                                            src={item.img || "/placeholder.svg"}
                                            alt={item.name}
                                            draggable={false}
                                            className="w-[100%] h-[auto] object-cover rounded-md select-none"
                                            style={{
                                                filter: isActive ? "none" : "brightness(0.7)",
                                                transition: "box-shadow 400ms ease, filter 400ms ease",
                                            }}
                                        /> */}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <button
                        onClick={goNext}
                        disabled={isAnimating}
                        className="text-primary hover:text-white text-xl bg-transparent border-none cursor-pointer disabled:opacity-50 transition-colors w-[10%] z-10"
                    >
                        ▶
                    </button>
                </div>
            </div>
        </div>
    );
}
